import React, { useEffect, useRef } from "react";

const GLYPHS = "virpluz:+-";
const CELL_X = 22;
const CELL_Y = 26;
const TICK = 110;

function hash(x, y) {
  const value = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return value - Math.floor(value);
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

const LetterField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    if (!context) return undefined;
    let frame = 0;
    let last = -Infinity;
    let visible = true;
    let running = true;

    const draw = (time) => {
      if (!running) return;
      const reduced = prefersReducedMotion();
      if (!reduced && visible && time - last < TICK) {
        frame = window.requestAnimationFrame(draw);
        return;
      }
      last = time;

      const bounds = canvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(bounds.width * ratio));
      const height = Math.max(1, Math.round(bounds.height * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }

      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, bounds.width, bounds.height);
      context.font = "15px ui-monospace, SFMono-Regular, Menlo, monospace";
      context.textAlign = "center";
      context.textBaseline = "middle";

      const seconds = reduced ? 0 : time / 1000;
      const cols = Math.ceil(bounds.width / CELL_X);
      const rows = Math.ceil(bounds.height / CELL_Y);
      const centerX = bounds.width / 2;
      const centerY = bounds.height / 2;

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const seed = hash(col, row);
          const x = col * CELL_X + CELL_X / 2;
          const y = row * CELL_Y + CELL_Y / 2;
          const band = Math.sin(col * 0.11 + row * 0.35 - seconds * 0.6) + Math.sin(col * 0.05 - seconds * 0.25);
          if (band + seed * 1.4 < 1.05) continue;

          const dx = (x - centerX) / (bounds.width * 0.4);
          const dy = (y - centerY) / (bounds.height * 0.5);
          const quiet = Math.min(1, Math.sqrt(dx * dx + dy * dy));
          const flicker = 0.5 + 0.5 * Math.sin(seconds * (2 + seed * 5) + seed * 40);
          const alpha = (0.12 + 0.6 * flicker * seed) * quiet;
          if (alpha < 0.04) continue;

          const glyph = GLYPHS[Math.floor((seed * 97 + Math.floor(seconds * (0.5 + seed))) % GLYPHS.length)];
          context.globalAlpha = alpha;
          context.fillStyle = seed > 0.86 ? "#d7f0c4" : "#9fbf98";
          context.fillText(glyph, x, y);
        }
      }

      context.globalAlpha = 1;
      if (!reduced && visible) frame = window.requestAnimationFrame(draw);
    };

    const restart = () => {
      window.cancelAnimationFrame(frame);
      last = -Infinity;
      frame = window.requestAnimationFrame(draw);
    };

    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const resize = new ResizeObserver(restart);
    const watch = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) restart();
    });
    motion.addEventListener("change", restart);
    resize.observe(canvas);
    watch.observe(canvas);
    draw(performance.now());

    return () => {
      running = false;
      window.cancelAnimationFrame(frame);
      motion.removeEventListener("change", restart);
      resize.disconnect();
      watch.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className="letter-field" aria-hidden="true" />;
};

export default LetterField;
